/**
 * EDITING ONE PARAGRAPH OF AN ORDER, IN PLACE.
 *
 * The order on screen is markdown rendered to HTML. To let an officer click a
 * paragraph and type, that one paragraph's markdown goes into a small rich-text
 * editor and comes back out — and "back out" is not the identity: the editor
 * escapes what markdown COULD read as syntax, so `[DIN to be entered]` returns
 * as `\[DIN to be entered\]` though nobody touched it. An order is a legal
 * document compared byte for byte by the checks behind it; noise like that must
 * never be saved.
 *
 * So three strings, not two:
 *
 *   source    the paragraph's markdown as published
 *   baseline  what the editor made of `source` BEFORE a key was pressed
 *   edited    what the editor holds now
 *
 * `baseline` and `edited` differ only where the officer typed. That stretch is
 * found (common head, common tail), mapped back onto `source`, and replaced
 * there. Every byte outside it is the published byte.
 */

export interface DocumentEdit {
  /** Offsets of the paragraph in the document it was rendered from. */
  start: number
  end: number
  /** `document.slice(start, end)` when the edit began — checked again before it is applied. */
  source: string
  /** The paragraph's new markdown. */
  markdown: string
}

/**
 * In an order a square bracket is a slot to be filled in and nothing else
 * (there are no links), so the editor's escapes on it come off again.
 */
function unescapeSlots(text: string): string {
  return text.replace(/\\([[\]])/g, "$1")
}

/**
 * Index in `source` that corresponds to index `upTo` in `baseline`, walking
 * both from the left. The editor only ever INSERTS a backslash before a
 * character it escapes; anything else that differs means the two do not line
 * up and the caller falls back (returns -1).
 */
function mapForward(source: string, baseline: string, upTo: number): number {
  let i = 0
  let j = 0
  while (i < upTo) {
    if (j < source.length && baseline[i] === source[j]) {
      i++
      j++
    } else if (baseline[i] === "\\") {
      i++
    } else {
      return -1
    }
  }
  return j
}

/** `source` with exactly the officer's change applied (module docstring). */
export function patchParagraph(source: string, baseline: string, edited: string): string {
  if (edited === baseline) return source
  let head = 0
  const max = Math.min(baseline.length, edited.length)
  while (head < max && baseline[head] === edited[head]) head++
  let tail = 0
  while (tail < max - head && baseline[baseline.length - 1 - tail] === edited[edited.length - 1 - tail]) tail++
  // Never cut an escape in half: a head ending on the editor's backslash, or a
  // tail starting right after one, would leave `\` or lose `[`.
  while (head > 0 && baseline[head - 1] === "\\") head--
  while (tail > 0 && baseline[baseline.length - tail - 1] === "\\") tail--

  const from = mapForward(source, baseline, head)
  // Where the untouched tail begins in `source`: the same left-to-right walk,
  // up to where the tail begins in `baseline` (one rule, one direction).
  const tailFrom = mapForward(source, baseline, baseline.length - tail)
  if (from < 0 || tailFrom < 0 || from > tailFrom) return unescapeSlots(edited)
  const typed = edited.slice(head, edited.length - tail)
  return source.slice(0, from) + unescapeSlots(typed) + source.slice(tailFrom)
}

/** The document with the edit applied, or `null` when the paragraph is no longer what was edited. */
export function applyDocumentEdit(document: string, edit: DocumentEdit): string | null {
  if (document.slice(edit.start, edit.end) !== edit.source) return null
  return document.slice(0, edit.start) + edit.markdown + document.slice(edit.end)
}
